import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Form, Input, Modal, Select, Typography, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

// Actions
import { get_zones } from '../../actions/areasActions';
import { file_create, file_update, file_upload, get_all_files, set_file_id } from '../../actions/filesActions';

// Utils
import isEmpty from '../../utils/isEmpty';

const FileForm = (props) => {
	const { messageApi, orgFilters, areaFilters, isModalOpen, setIsModalOpen, update, fileToUpdate, setFileToUpdate, pageSize, skip, setSkip } = props;

	const dispatch = useDispatch();

	const { global_data } = useSelector(state => state.global);

	const { organizations, workspaces } = useSelector(state => state.organization);

	const { regions, zones, depots, groups } = useSelector(state => state.areas);

	const { file_create_loading, all_files, file_id, files_errors } = useSelector(state => state.trainingFiles);

	const [formFields, setFormFields] = useState({});
	const [fileList, setFileList] = useState([]);
	
	const [form] = Form.useForm();

	useEffect(() => {
		if (!isEmpty(fileToUpdate)) {
			// If there is information in fileToUpdate, we use it to update the form.
			const formFields = {
				organization: fileToUpdate.organization?._id?.$oid,
				workspace: fileToUpdate.workspace?._id?.$oid,
				region: fileToUpdate.region?._id?.$oid,
				zone: fileToUpdate.zone?._id?.$oid,
				name: fileToUpdate.name,
				description: fileToUpdate.description,
			};
	
			form.setFieldsValue(formFields);
			setFormFields(formFields);
		}
		else {
			// If fileToUpdate is empty, we use the organization and area filters.
			form.setFieldsValue({
				organization: orgFilters?.organization || null,
				workspace: orgFilters?.workspace || null,
				region: areaFilters?.region || null,
				zone: areaFilters?.zone || null,
				file: [],
			});
		}
	}, [orgFilters, areaFilters, fileToUpdate]);

	const handleCancel = (finish) => {
		form.resetFields();
		setFileList([]);
		setIsModalOpen(false);
		setFileToUpdate({});
		dispatch(set_file_id(null));

		if (finish) {
			const newSkip = !update 
				? Math.floor(all_files.count / pageSize) * pageSize 
				: skip;

			setSkip(newSkip);
			const filters = { ...orgFilters, ...areaFilters, skip: newSkip, limit: pageSize };
			dispatch(get_all_files(filters));
		}
	}

	const onValuesChange = (changedValues, allValues) => {
		if (changedValues.region) {
			// Reset zone field value
			form.setFieldsValue({ zone: undefined });

			// Get zones
			const filters = {
				organization: orgFilters.organization,
				workspace: orgFilters.workspace,
				region: changedValues.region,
				skip: 0,
				limit: 0
			};
			dispatch(get_zones(filters));
		}
	}

	const onOk = () => {
		form
			.validateFields()
			.then((values) => {
				if (!update) {
					// Create
					const fileBody = {
						organization: values.organization !== '' ? values.organization : null,
						workspace: values.workspace !== '' ? values.workspace : null,
						region: values.region !== '' ? values.region : null,
						zone: values.zone !== '' ? values.zone : null,
						name: values.name,
						description: values.description || values.name,
					}
					dispatch(file_create(fileBody));
				}
				else {
					// Update
					if (formFields.name !== values.name || formFields.description !== values.description) {
						const fileBody = {
							name: values.name,
							description: values.description
						}
						dispatch(file_update(messageApi, file_id, fileBody, () => handleCancel(true)));
					}
					else {
						handleCancel(true);
					}
				}
			})
			.catch((info) => {
				console.error(info);
			});
	}

	const uploadButton = (
		<button
			style={{ border: 0, background: 'none' }}
			type='button'
		>
			<div>
				<InboxOutlined style={{ color: '#42506b', fontSize: 40, marginBottom: '0.5rem' }}/>
				<Typography.Paragraph style={{ marginBottom: 0 }}>{global_data?.files?.formInputUploadInstuction}</Typography.Paragraph>
				<Typography.Paragraph style={{ marginBottom: 0 }} type='secondary'>{global_data?.files?.formInputUploadWarning}</Typography.Paragraph>
			</div>
		</button>
	);

	const handleChange = ({ file, fileList: newFileList }) => {
		if (file.status === 'error') {
			setFileList([
				{
					status: 'error',
					uid: file.uid,
					name: file.name
				}
			]);
		}
		else {
			setFileList(newFileList);
		}
	}

	return (
		<Modal
			title={!update ? global_data?.files?.createFormTitle : global_data?.files?.updateFormTitle}
			centered
			open={isModalOpen}
			onCancel={() => handleCancel(false)}
			maskClosable={false}
			width={700}
			footer={[
				<Button
					key='submit'
					type='primary'
					loading={file_create_loading}
					onClick={file_id && !update ? () => handleCancel(true) : onOk}
				>
					{file_id ? global_data?.actionButtons?.finish : global_data?.actionButtons?.save}
				</Button>,
			]}
		>
			<Form
				labelCol={{ span: 5 }}
				form={form}
				onValuesChange={onValuesChange}
			>
				<Form.Item
					label={global_data?.organizationTerms?.organization}
					name='organization'
					hidden={orgFilters.organization !== ''}
					rules={[
						{
							required: true,
							message: global_data?.form?.requiredInputMsg,
						}
					]}
				>
					<Select
						showSearch
						disabled={update}
						placeholder={`${global_data?.form?.selectPlaceholder} ${global_data?.organizationTerms?.organization}`}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={organizations.map(option => ({ value: option._id.$oid, label: option.name }))}
					/>
				</Form.Item>
				<Form.Item
					label={global_data?.organizationTerms?.workspace}
					name='workspace'
					hidden={orgFilters.workspace !== ''}
					rules={[
						{
							required: true,
							message: global_data?.form?.requiredInputMsg,
						}
					]}
				>
					<Select
						showSearch
						disabled={update}
						placeholder={`${global_data?.form?.selectPlaceholder} ${global_data?.organizationTerms?.workspace}`}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={workspaces.map(option => ({ value: option._id.$oid, label: option.name }))}
					/>
				</Form.Item>
				<Form.Item
					label={global_data?.organizationTerms?.region}
					name='region'
					hidden={areaFilters.region !== ''}
				>
					<Select
						showSearch
						disabled={update}
						placeholder={`${global_data?.form?.selectPlaceholder} ${global_data?.organizationTerms?.region}`}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={regions.regions.map(option => ({ value: option._id.$oid, label: option.name }))}
					/>
				</Form.Item>
				<Form.Item
					label={global_data?.organizationTerms?.zone}
					name='zone'
					hidden={areaFilters.zone !== ''}
				>
					<Select
						showSearch
						disabled={update}
						placeholder={`${global_data?.form?.selectPlaceholder} ${global_data?.organizationTerms?.zone}`}
						optionFilterProp='children'
						filterOption={(input, option) => (option?.label?.toLowerCase() ?? '').includes(input?.toLowerCase())}
						options={zones.zones.map(option => ({ value: option._id.$oid, label: option.name }))}
					/>
				</Form.Item>
				<Form.Item
					label={global_data?.files?.formInputName}
					name='name'
					rules={[
						{
							required: true,
							message: global_data?.form?.requiredInputMsg,
						}
					]}
				>
					<Input disabled={update} placeholder={`${global_data?.form?.inputPlaceholder} ${global_data?.files?.formInputName}`} />
				</Form.Item>
				<Form.Item
					label={global_data?.files?.formInputDescription}
					name='description'
				>
					<Input disabled={update} placeholder={`${global_data?.form?.inputPlaceholder} ${global_data?.files?.formInputDescription}`} />
				</Form.Item>
				{file_id && ( 
					<Form.Item
						label={global_data?.files?.formInputFile}
					>
						<Upload
							name='avatar'
							listType='picture-card'
							className='avatar-uploader'
							fileList={fileList}
							action={`${process.env.REACT_APP_SERVER_URL}/api/training/files/${file_id}/upload`}
							customRequest={(options) => dispatch(file_upload(options))}
							onChange={handleChange}
							showUploadList={{
								showPreviewIcon: true,
								showRemoveIcon: false,
								showDownloadIcon: false
							}}
						>
							{fileList.length > 0 ? null : uploadButton}
						</Upload>
					</Form.Item>
				)}
			</Form>
		</Modal>
	)
}

FileForm.propTypes = {
	messageApi: PropTypes.object.isRequired,
	orgFilters: PropTypes.object.isRequired,
	areaFilters: PropTypes.object.isRequired,
	isModalOpen: PropTypes.bool.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	update: PropTypes.bool.isRequired,
	fileToUpdate: PropTypes.object.isRequired,
	setFileToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default FileForm;
